import React from 'react';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import {
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Spell } from '../../types';
import { SpellSchoolChip } from './SpellSchoolChip';
import { createParchmentSurface, hashStringToSeed, parchmentUnderlineSx } from '../../theme/surfaces';
import { ConfirmActionDialog } from './ConfirmActionDialog';
import { SpellCard } from './SpellCard';

interface SpellCardMiniProps {
  spell: Spell;
  onDelete?: (spell: Spell) => void;
  deleteDisabled?: boolean;
  unusedLevelUps?: number;
  onImprove?: (spell: Spell) => Promise<void>;
}

export const SpellCardMini = React.memo(function SpellCardMini({
  spell,
  onDelete,
  deleteDisabled = false,
  unusedLevelUps,
  onImprove,
}: SpellCardMiniProps) {
  const [isImproveConfirmOpen, setIsImproveConfirmOpen] = React.useState(false);
  const [isImproving, setIsImproving] = React.useState(false);
  const [isExpanded, setIsExpanded] = React.useState(false);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const canImprove = !deleteDisabled && (unusedLevelUps ?? 0) > 0 && Boolean(onImprove);
  const parchmentSx = React.useMemo(() => createParchmentSurface(hashStringToSeed(spell.id)), [spell.id]);

  const handleImproveConfirm = async () => {
    if (!onImprove) return;
    setIsImproving(true);
    try {
      await onImprove(spell);
    } finally {
      setIsImproving(false);
      setIsImproveConfirmOpen(false);
    }
  };

  return (
    <Card elevation={0} sx={{ ...parchmentSx, filter: 'drop-shadow(0 3px 6px rgba(40, 20, 5, 0.25))', width: 257 }}>
      <CardContent>
        <Stack direction="column" justifyContent="space-between" alignItems="flex-start" spacing={1}>
          <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }} width="100%">
            <Typography variant="subtitle1" sx={{ fontWeight: 700, ...parchmentUnderlineSx }}>
              {spell.name}
            </Typography>
            <Stack direction="row">
              {canImprove && (
                <FileUploadIcon fontSize="small"
                  sx={{ backgroundColor: alpha("#FFD700", 0.55) }}
                />
              )}
              <IconButton
                aria-label={`Expand ${spell.name}`}
                size="small"
                onClick={() => setIsExpanded(true)}
              >
                <OpenInFullIcon fontSize="inherit" />
              </IconButton>
            </Stack>
          </Stack>
          <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }} width="100%">
            <SpellSchoolChip label={spell.school} />
            <Typography variant="subtitle1" color="textSecondary" sx={{ whiteSpace: 'nowrap' }}>
              {spell.castingNumber}  ({spell.castingNumber + 2})
            </Typography>
          </Stack>
        </Stack>
      </CardContent>

      {canImprove && (
        <ConfirmActionDialog
          open={isImproveConfirmOpen}
          title="Improve Spell"
          message={`Spend a level-up to improve ${spell.name}? Its casting number will decrease from ${spell.castingNumber} to ${spell.castingNumber - 1}.`}
          confirmLabel="Improve"
          busy={isImproving}
          onClose={() => { if (!isImproving) setIsImproveConfirmOpen(false); }}
          onConfirm={handleImproveConfirm}
        />
      )}

      <Dialog
        open={isExpanded}
        onClose={() => setIsExpanded(false)}
        fullScreen={fullScreen}
        maxWidth="md"
        className="MemberDialog"
        disableRestoreFocus
        sx={{ m: 0, p: 0, border: 0 }}
      >
        <DialogContent sx={{ m: 1, p: 2, pt:6, alignItems: 'center', display: 'flex', justifyContent: 'center' }}>
          <SpellCard
            spell={spell}
            onDelete={onDelete}
            deleteDisabled={deleteDisabled}
            unusedLevelUps={unusedLevelUps}
            onImprove={onImprove}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsExpanded(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
});
